import { REPORT_REASON_LABELS } from '@bienenkampf/shared';
import { Badge, Button, Card, Input } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { formatDateTime } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

/** Admin-UI (Konzept 4/11): Welten, Live-Balancing mit Aenderungssatz, Meldungen. */
export function AdminPage() {
  const trpc = useTRPC();
  const me = useQuery(trpc.auth.me.queryOptions());
  const worlds = useQuery({
    ...trpc.admin.worlds.queryOptions(),
    enabled: !!me.data?.account?.isAdmin,
  });
  const [worldId, setWorldId] = useState<number | null>(null);
  if (me.isPending) return <p className="text-stone-400">Lade…</p>;
  if (!me.data?.account) return <Navigate to="/login" />;
  if (!me.data.account.isAdmin)
    return (
      <p className="text-red-400">Kein Admin-Zugang. Freischaltung per Ops-Task „make-admin“.</p>
    );
  const selected = worldId ?? worlds.data?.[0]?.id ?? null;
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Admin</h1>
      <WorldsCard worlds={worlds.data ?? []} selected={selected} onSelect={setWorldId} />
      {selected !== null && (
        <>
          <WorldSettingsCard world={(worlds.data ?? []).find((w) => w.id === selected)} />
          <ReportsCard worldId={selected} />
          <BalanceCard worldId={selected} />
          <ChangeLogCard worldId={selected} />
        </>
      )}
    </div>
  );
}

type AdminWorld = {
  id: number;
  name: string;
  status: string;
  speed: number;
  startedAt: Date;
  protectionHours: number;
  fieldDwellMinutes: number;
  fieldSlots: number;
  stats: {
    players: number;
    hives: number;
    alliances: number;
    pendingEvents: number;
    failedEvents: number;
    openReports: number;
  };
};

function WorldsCard({
  worlds,
  selected,
  onSelect,
}: {
  worlds: AdminWorld[];
  selected: number | null;
  onSelect: (id: number) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const setStatus = useMutation(
    trpc.admin.setWorldStatus.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.admin.pathKey() }),
    }),
  );
  const retry = useMutation(
    trpc.admin.retryFailedEvents.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.admin.pathKey() }),
    }),
  );
  return (
    <Card title="Welten">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-stone-400">
            <tr>
              <th className="py-1">Welt</th>
              <th>Status</th>
              <th>Speed</th>
              <th>Spieler</th>
              <th>Stöcke</th>
              <th>Stände</th>
              <th>Events offen/fehlgeschlagen</th>
              <th>Meldungen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {worlds.map((w) => (
              <tr
                key={w.id}
                className={`border-t border-stone-800 ${selected === w.id ? 'bg-stone-900' : ''}`}
              >
                <td className="py-1">
                  <button type="button" className="text-amber-300" onClick={() => onSelect(w.id)}>
                    {w.name}
                  </button>
                  <span className="ml-1 text-xs text-stone-500">
                    #{w.id} · seit {formatDateTime(w.startedAt)}
                  </span>
                </td>
                <td>
                  <select
                    className="rounded-md border border-stone-700 bg-stone-900 px-1 py-0.5 text-xs"
                    value={w.status}
                    onChange={(e) =>
                      setStatus.mutate({
                        worldId: w.id,
                        status: e.target.value as 'OPEN' | 'CLOSED' | 'ARCHIVED',
                      })
                    }
                  >
                    {['OPEN', 'CLOSED', 'ARCHIVED'].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{w.speed}</td>
                <td>{w.stats.players}</td>
                <td>{w.stats.hives}</td>
                <td>{w.stats.alliances}</td>
                <td>
                  {w.stats.pendingEvents} /{' '}
                  <span className={w.stats.failedEvents > 0 ? 'text-red-400' : ''}>
                    {w.stats.failedEvents}
                  </span>
                </td>
                <td>
                  {w.stats.openReports > 0 ? <Badge tone="red">{w.stats.openReports}</Badge> : 0}
                </td>
                <td>
                  {w.stats.failedEvents > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => retry.mutate({ worldId: w.id })}
                      loading={retry.isPending}
                    >
                      Fehlgeschlagene erneut
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function WorldSettingsCard({ world }: { world: AdminWorld | undefined }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [hours, setHours] = useState('');
  const [dwell, setDwell] = useState('');
  const [slots, setSlots] = useState('');
  const [apply, setApply] = useState(true);
  const save = useMutation(
    trpc.admin.setWorldSettings.mutationOptions({
      onSuccess: () => {
        setHours('');
        setDwell('');
        setSlots('');
        void qc.invalidateQueries({ queryKey: trpc.admin.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.world.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.auth.me.queryKey() });
      },
    }),
  );
  if (!world) return null;
  const h = hours === '' ? undefined : Number(hours);
  const d = dwell === '' ? undefined : Number(dwell);
  const sl = slots === '' ? undefined : Number(slots);
  return (
    <Card title={`Welteinstellungen – ${world.name}`}>
      <p className="mb-2 text-xs text-stone-400">
        Imkerschutz gilt für neue Beiträge und Neuanfänge. Mit „auf laufende Fristen anwenden“ wird
        der Schutz bestehender Spieler auf Beitritt + neue Stunden gekürzt (nie verlängert).
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        <Input
          name="protectionHours"
          type="number"
          label={`Imkerschutz (h) – aktuell ${world.protectionHours}`}
          min={0}
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder={String(world.protectionHours)}
        />
        <Input
          name="fieldDwellMinutes"
          type="number"
          label={`Verweildauer am Feld (min) – aktuell ${world.fieldDwellMinutes}`}
          min={1}
          value={dwell}
          onChange={(e) => setDwell(e.target.value)}
          placeholder={String(world.fieldDwellMinutes)}
        />
        <Input
          name="fieldSlots"
          type="number"
          label={`Plätze je Feld – aktuell ${world.fieldSlots}`}
          min={1}
          value={slots}
          onChange={(e) => setSlots(e.target.value)}
          placeholder={String(world.fieldSlots)}
        />
      </div>
      <label className="mt-2 flex items-center gap-2 text-xs text-stone-300">
        <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} />
        Imkerschutz auf laufende Fristen anwenden
      </label>
      <div className="mt-2 flex items-center gap-2">
        <Button
          size="sm"
          disabled={h === undefined && d === undefined && sl === undefined}
          loading={save.isPending}
          onClick={() =>
            save.mutate({
              worldId: world.id,
              ...(h !== undefined ? { protectionHours: h } : {}),
              ...(d !== undefined ? { fieldDwellMinutes: d } : {}),
              ...(sl !== undefined ? { fieldSlots: sl } : {}),
              applyProtectionToPlayers: apply,
            })
          }
        >
          Speichern
        </Button>
        {save.data && (
          <span className="text-xs text-emerald-400">
            Gespeichert
            {save.data.adjusted > 0 && `, ${save.data.adjusted} laufende Schutzfristen gekürzt`}.
          </span>
        )}
        {save.error && <span className="text-xs text-red-400">{save.error.message}</span>}
      </div>
    </Card>
  );
}

function ReportsCard({ worldId }: { worldId: number }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const reports = useQuery(trpc.admin.reports.queryOptions({ worldId }));
  const resolve = useMutation(
    trpc.admin.resolveReport.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.admin.pathKey() }),
    }),
  );
  return (
    <Card title={`Meldungen (${reports.data?.length ?? 0} offen)`}>
      {reports.data?.length === 0 && (
        <p className="text-sm text-stone-400">Keine offenen Meldungen.</p>
      )}
      <ul className="flex flex-col gap-2 text-sm">
        {reports.data?.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-stone-900 p-2">
            <Badge tone="red">{REPORT_REASON_LABELS[r.reason] ?? r.reason}</Badge>
            <span>
              <b>{r.reporterName}</b> meldet <b>{r.reportedName}</b>
            </span>
            <span className="text-xs text-stone-400">{formatDateTime(r.createdAt)}</span>
            {r.message && <span className="w-full text-xs text-stone-300">„{r.message}“</span>}
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={() => resolve.mutate({ id: r.id })}
              loading={resolve.isPending}
            >
              Erledigt
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function BalanceCard({ worldId }: { worldId: number }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const balance = useQuery(trpc.admin.balance.queryOptions({ worldId }));
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const save = useMutation(
    trpc.admin.setBalance.mutationOptions({
      onSuccess: () => {
        setEditing(null);
        void qc.invalidateQueries({ queryKey: trpc.admin.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.world.pathKey() });
      },
    }),
  );
  const rows = (balance.data ?? []).filter(
    (r) => !filter || r.key.toLowerCase().includes(filter.toLowerCase()),
  );
  return (
    <Card title="Balancing (live)">
      <p className="mb-2 text-xs text-stone-400">
        Änderungen gelten sofort für neue Berechnungen (Cache max. 30 s) und werden im Änderungssatz
        protokolliert. Werte als JSON, z. B. <code>0.3</code> oder{' '}
        <code>{'{"nectar":800,"pollen":600,"wax":150}'}</code>.
      </p>
      <Input
        name="filter"
        label="Schlüssel filtern"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="z. B. queen. oder units.WARRIOR"
      />
      <div className="mt-2 max-h-[28rem] overflow-auto">
        <table className="w-full text-xs">
          <tbody>
            {rows.slice(0, 300).map((r) => (
              <tr
                key={r.key}
                className={`border-t border-stone-800 ${r.changed ? 'bg-amber-950/30' : ''}`}
              >
                <td className="py-1 pr-2 font-mono">{r.key}</td>
                <td className="py-1 pr-2 font-mono text-stone-300">
                  {editing === r.key ? (
                    <input
                      className="w-full rounded-md border border-stone-700 bg-stone-950 px-1 py-0.5 font-mono"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                    />
                  ) : (
                    JSON.stringify(r.value ?? r.defaultValue)
                  )}
                </td>
                <td className="py-1 pr-2 text-stone-500">
                  {r.changed && `Default ${JSON.stringify(r.defaultValue)}`}
                </td>
                <td className="py-1 text-right">
                  {editing === r.key ? (
                    <>
                      <Button
                        size="sm"
                        loading={save.isPending}
                        onClick={() => {
                          try {
                            save.mutate({
                              worldId,
                              key: r.key,
                              value: JSON.parse(draft) as unknown,
                            });
                          } catch {
                            alert('Kein gültiges JSON');
                          }
                        }}
                      >
                        Speichern
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        ✕
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditing(r.key);
                        setDraft(JSON.stringify(r.value ?? r.defaultValue));
                      }}
                    >
                      Ändern
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {save.error && <p className="mt-2 text-sm text-red-400">{save.error.message}</p>}
    </Card>
  );
}

function ChangeLogCard({ worldId }: { worldId: number }) {
  const trpc = useTRPC();
  const log = useQuery(trpc.admin.changeLog.queryOptions({ worldId }));
  if (!log.data || log.data.length === 0) return null;
  return (
    <Card title="Änderungssatz">
      <p className="mb-2 text-xs text-stone-400">
        Zum Zurückschreiben ins Repo (packages/shared/src/balance.ts) – neueste zuerst.
      </p>
      <ul className="flex flex-col gap-1 font-mono text-xs">
        {log.data.map((c) => (
          <li key={c.id}>
            <span className="text-stone-500">{formatDateTime(c.createdAt)}</span> {c.key}:{' '}
            <span className="text-red-300">{JSON.stringify(c.oldValue)}</span> →{' '}
            <span className="text-emerald-300">{JSON.stringify(c.newValue)}</span>
            <span className="text-stone-500"> ({c.changedBy ?? '–'})</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
